import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Plus, X, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';

const fmtCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const getFYOptions = () => {
  const now = new Date();
  const cy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const opts = [];
  for (let i = 0; i < 3; i++) {
    const sy = cy - i;
    opts.push({ label: `FY ${String(sy).slice(2)}-${String(sy + 1).slice(2)}`, start: `${sy}-04-01`, end: `${sy + 1}-03-31` });
  }
  return opts;
};

const AgentSalesByParty = ({ user }) => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const fyOptions = getFYOptions();
  const [activeFY, setActiveFY] = useState(fyOptions[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [compareReports, setCompareReports] = useState([]); // [{label, parties, totals}]

  const loadReport = useCallback(async (fy) => {
    setLoading(true);
    try {
      const res = await api.get(`/agents/${agentId}/sales-by-party?start_date=${fy.start}&end_date=${fy.end}`);
      setReport(res.data);
    } catch { toast.error('Failed to load sales report'); }
    finally { setLoading(false); }
  }, [agentId]);

  useEffect(() => { loadReport(activeFY); }, [agentId, loadReport, activeFY]);

  const handleFYChange = (fy) => { setActiveFY(fy); setShowCustomRange(false); };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) { toast.error('Pick both start and end dates'); return; }
    if (customStart > customEnd) { toast.error('Start must be before end'); return; }
    const fmt = (d) => { const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}`; };
    setActiveFY({ label: `${fmt(customStart)} – ${fmt(customEnd)}`, start: customStart, end: customEnd, isCustom: true });
  };

  const addCompareFY = async (fy) => {
    if (compareReports.find(r => r.label === fy.label)) { toast.info('Already added'); return; }
    if (compareReports.length >= 4) { toast.error('Max 4 periods to compare'); return; }
    try {
      const res = await api.get(`/agents/${agentId}/sales-by-party?start_date=${fy.start}&end_date=${fy.end}`);
      setCompareReports(prev => [...prev, { label: fy.label, parties: res.data.parties, totals: res.data.totals }]);
    } catch { toast.error('Failed to load comparison data'); }
  };

  const removeCompare = (label) => setCompareReports(prev => prev.filter(r => r.label !== label));

  const openLedger = (party) => {
    navigate(`/ledger-report?type=${party.type}&id=${party.id}&start=${activeFY.start}&end=${activeFY.end}`);
  };

  // Build merged comparison table when in compare mode
  const buildCompareMatrix = () => {
    const partyKeyMap = {};
    const baseLabel = activeFY.label;
    const allPeriods = [{ label: baseLabel, parties: report?.parties || [], totals: report?.totals }, ...compareReports];
    allPeriods.forEach(period => {
      period.parties.forEach(p => {
        const key = `${p.type}:${p.id}`;
        if (!partyKeyMap[key]) partyKeyMap[key] = { id: p.id, type: p.type, name: p.name, city: p.city, periods: {} };
        partyKeyMap[key].periods[period.label] = p.net_sales;
      });
    });
    const rows = Object.values(partyKeyMap).sort((a, b) => {
      const aMax = Math.max(...Object.values(a.periods));
      const bMax = Math.max(...Object.values(b.periods));
      return bMax - aMax;
    });
    return { rows, periods: allPeriods };
  };

  if (loading && !report) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  if (!report) return null;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="agent-sales-by-party">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate('/agents')} data-testid="back-to-agents-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>{report.agent.name}</h1>
          <p className="text-gray-600 mt-1 text-sm">
            {[report.agent.phone, report.agent.area, report.agent.city].filter(Boolean).join(' | ')}
            <span className="ml-2 text-gray-400">— Sales by Party</span>
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 mr-2">PERIOD:</span>
            {fyOptions.map(fy => (
              <Button key={fy.label} size="sm"
                variant={activeFY.label === fy.label ? 'default' : 'outline'}
                className={activeFY.label === fy.label ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => handleFYChange(fy)} data-testid={`fy-${fy.label}`}>{fy.label}</Button>
            ))}
            <Button size="sm" variant={activeFY.isCustom || showCustomRange ? 'default' : 'outline'}
              className={activeFY.isCustom || showCustomRange ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              onClick={() => setShowCustomRange(v => !v)} data-testid="fy-custom">Custom</Button>
            {showCustomRange && (
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md border" data-testid="custom-range-picker">
                <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-8 w-40 text-xs" data-testid="custom-start" />
                <span className="text-xs text-gray-500">to</span>
                <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-8 w-40 text-xs" data-testid="custom-end" />
                <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={applyCustomRange} data-testid="apply-custom">Apply</Button>
              </div>
            )}
            {activeFY.isCustom && !showCustomRange && <span className="text-xs text-gray-600 italic ml-1">{activeFY.label}</span>}
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant={compareMode ? 'default' : 'outline'}
                className={compareMode ? 'bg-purple-600 hover:bg-purple-700' : ''}
                onClick={() => setCompareMode(v => !v)} data-testid="toggle-compare">
                <BarChart3 className="w-4 h-4 mr-1" /> {compareMode ? 'Exit Compare' : 'Compare FY'}
              </Button>
            </div>
          </div>
          {compareMode && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <span className="text-xs font-semibold text-gray-500">ADD PERIOD:</span>
              {fyOptions.filter(fy => fy.label !== activeFY.label && !compareReports.find(r => r.label === fy.label)).map(fy => (
                <Button key={fy.label} size="sm" variant="outline" onClick={() => addCompareFY(fy)} data-testid={`add-compare-${fy.label}`}>
                  <Plus className="w-3 h-3 mr-1" /> {fy.label}
                </Button>
              ))}
              {compareReports.map(r => (
                <Badge key={r.label} className="bg-purple-100 text-purple-700 gap-1 cursor-pointer" onClick={() => removeCompare(r.label)} data-testid={`remove-compare-${r.label}`}>
                  {r.label} <X className="w-3 h-3" />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary cards (only in non-compare mode) */}
      {!compareMode && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Parties</p><p className="text-lg font-bold text-gray-800">{report.totals.party_count}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Sales</p><p className="text-lg font-bold text-indigo-700">{fmtCurrency(report.totals.total_sales)}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Returns</p><p className="text-lg font-bold text-red-600">{fmtCurrency(report.totals.total_returns)}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Net Sales</p><p className="text-lg font-bold text-emerald-700">{fmtCurrency(report.totals.net_sales)}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500"># Bills</p><p className="text-lg font-bold text-gray-800">{report.totals.bill_count}</p></CardContent></Card>
        </div>
      )}

      {/* TABLE */}
      <Card>
        <CardContent className="p-0">
          {compareMode ? (() => {
            const { rows, periods } = buildCompareMatrix();
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="compare-table">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 text-left font-medium">Party</th>
                    <th className="px-3 py-3 text-center text-xs font-medium">Type</th>
                    {periods.map(p => <th key={p.label} className="px-3 py-3 text-right text-xs font-medium">{p.label}</th>)}
                    <th className="px-3 py-3 text-center text-xs font-medium">Action</th>
                  </tr></thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={`${row.type}:${row.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs font-medium text-gray-800">{row.name}<span className="block text-[10px] text-gray-400">{row.city}</span></td>
                        <td className="px-3 py-2 text-center"><Badge className="text-[9px] bg-gray-200 text-gray-600">{row.type}</Badge></td>
                        {periods.map(p => (
                          <td key={p.label} className="px-3 py-2 text-right text-xs">
                            {row.periods[p.label] !== undefined ? fmtCurrency(row.periods[p.label]) : <span className="text-gray-300">-</span>}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-center">
                          <Button size="sm" variant="ghost" className="text-emerald-600 h-7 px-2" onClick={() => openLedger(row)} data-testid={`ledger-${row.id}`}>
                            <BookOpen className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 border-t-2 border-gray-300">
                      <td className="px-3 py-3 text-right text-xs font-bold text-gray-700" colSpan={2}>Net Total</td>
                      {periods.map(p => <td key={p.label} className="px-3 py-3 text-right text-xs font-bold text-emerald-700">{fmtCurrency(p.totals?.net_sales || 0)}</td>)}
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })() : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="party-sales-table">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-left text-xs font-medium">Party</th>
                  <th className="px-3 py-3 text-center text-xs font-medium">Type</th>
                  <th className="px-3 py-3 text-left text-xs font-medium">City</th>
                  <th className="px-3 py-3 text-center text-xs font-medium"># Bills</th>
                  <th className="px-3 py-3 text-right text-xs font-medium">Total Sales</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-red-600">Returns</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-emerald-700">Net Sales</th>
                  <th className="px-3 py-3 text-center text-xs font-medium">Ledger</th>
                </tr></thead>
                <tbody>
                  {report.parties.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">No sales found for this agent in the selected period.</td></tr>
                  ) : report.parties.map(p => (
                    <tr key={`${p.type}:${p.id}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`party-row-${p.id}`}>
                      <td className="px-3 py-2 text-xs font-medium text-gray-800">{p.name}</td>
                      <td className="px-3 py-2 text-center"><Badge className="text-[9px] bg-gray-200 text-gray-600">{p.type}</Badge></td>
                      <td className="px-3 py-2 text-xs text-gray-500">{p.city || '-'}</td>
                      <td className="px-3 py-2 text-center text-xs">{p.bill_count}</td>
                      <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(p.total_sales)}</td>
                      <td className="px-3 py-2 text-right text-xs text-red-600">{p.total_returns > 0 ? `-${fmtCurrency(p.total_returns)}` : '-'}</td>
                      <td className="px-3 py-2 text-right text-xs font-bold text-emerald-700">{fmtCurrency(p.net_sales)}</td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" onClick={() => openLedger(p)} data-testid={`ledger-btn-${p.id}`}>
                          <BookOpen className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {report.parties.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-100 border-t-2 border-gray-300">
                      <td colSpan={3} className="px-3 py-3 text-right text-xs font-bold text-gray-700">Total</td>
                      <td className="px-3 py-3 text-center text-xs font-bold">{report.totals.bill_count}</td>
                      <td className="px-3 py-3 text-right text-xs font-bold">{fmtCurrency(report.totals.total_sales)}</td>
                      <td className="px-3 py-3 text-right text-xs font-bold text-red-600">{report.totals.total_returns > 0 ? `-${fmtCurrency(report.totals.total_returns)}` : '-'}</td>
                      <td className="px-3 py-3 text-right text-xs font-bold text-emerald-700">{fmtCurrency(report.totals.net_sales)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 italic">
        Tip: Need to free up space from very old FYs? Admins can archive a financial year via Admin Tools → Archive FY. Archived FYs are stored separately and won't appear in normal reports.
      </p>
    </div>
  );
};

export default AgentSalesByParty;
